/**
 * Bill Detail Page
 *
 * Comprehensive view of a bill with all information:
 * - Bill info (type, status, dates)
 * - Client info
 * - Line items table
 * - Totals
 * - Deposits (for credit bills)
 * - Hacienda status and submission history
 * - Exoneration info
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Printer,
  FileText,
  User,
  CreditCard,
  Receipt,
  AlertCircle,
  Plus,
  Minus,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  Pencil,
  Trash2,
  Loader2,
  Link2,
  ExternalLink,
} from 'lucide-react'

import { useBill, useBillSubmissions, useSubmitToHacienda, useInvoiceDetails, useCreditNoteDetails, useTicketDetails, useCancelBill, useUpdateBillNote, useUpdateBillReceiverActivity, hasFinalHaciendaStatus } from '@/hooks/useBills'
import { ActivityCodeSelect } from '@/components/clients/ActivityCodeSelect'
import { useTenant } from '@/hooks/useTenant'
import { useActivityCatalog } from '@/hooks/useValidActivityCodes'
import { formatCurrency, formatDate, formatDateTime, cn, PAYMENT_TYPE_LABELS as DEPOSIT_PAYMENT_LABELS } from '@/lib/utils'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { useToast } from '@/components/ui/toaster'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { HaciendaStatusCard } from '@/components/bills/HaciendaStatusCard'
import {
  BILL_TYPE_LABELS,
  BILL_STATUS_LABELS,
  BILL_STATUS_COLORS,
  PAYMENT_TYPE_LABELS,
  PAYMENT_METHOD_CODES,
  EXONERATION_TYPE_LABELS,
  BillStatus,
  BillType,
  ExonerationType,
} from '@/types'

export function BillDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const billId = id && !isNaN(Number(id)) ? Number(id) : 0

  const { data: bill, isLoading, error, refetch } = useBill(billId)
  const { data: submissions } = useBillSubmissions(billId)
  const submitMutation = useSubmitToHacienda()
  const deleteMutation = useCancelBill()
  const { tenant } = useTenant()
  const { data: catalogData } = useActivityCatalog()

  // Get real-time Hacienda status from Alanube
  // Use the correct endpoint based on document type
  const isCreditNote = bill?.bill_type === BillType.CREDIT_NOTE
  const isTicket = bill?.bill_type === BillType.TICKET
  const detailsOptions = {
    auto: !hasFinalHaciendaStatus(bill?.hacienda_status),
    knownStatus: bill?.hacienda_status,
  }
  const { data: invoiceDetailsData } = useInvoiceDetails(
    !isCreditNote && !isTicket ? bill?.alanube_document_id : null,
    detailsOptions,
  )
  const { data: creditNoteDetailsData } = useCreditNoteDetails(
    isCreditNote ? bill?.alanube_document_id : null,
    detailsOptions,
  )
  const { data: ticketDetailsData } = useTicketDetails(
    isTicket ? bill?.alanube_document_id : null,
    detailsOptions,
  )
  const invoiceDetails = isCreditNote
    ? creditNoteDetailsData
    : isTicket
      ? ticketDetailsData
      : invoiceDetailsData

  const [linesExpanded, setLinesExpanded] = useState(true)
  const [noteEditing, setNoteEditing] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const updateNoteMutation = useUpdateBillNote()

  const startEditNote = () => {
    setNoteDraft(bill?.notes ?? '')
    setNoteEditing(true)
  }

  const cancelEditNote = () => {
    setNoteEditing(false)
    setNoteDraft('')
  }

  const saveNote = async () => {
    try {
      const trimmed = noteDraft.trim()
      await updateNoteMutation.mutateAsync({
        billId,
        notes: trimmed || null,
      })
      toast({ title: t('bills.clientNoteSaved') })
      setNoteEditing(false)
    } catch {
      toast({
        title: t('bills.clientNoteError'),
        variant: 'destructive',
      })
    }
  }

  const updateReceiverActivityMutation = useUpdateBillReceiverActivity()

  const saveReceiverActivity = async (code: string) => {
    try {
      await updateReceiverActivityMutation.mutateAsync({
        billId,
        receiverActivityCode: code || null,
      })
      toast({ title: t('activities.receiverActivitySaved') })
    } catch {
      toast({ title: t('activities.receiverActivityError'), variant: 'destructive' })
    }
  }

  const handlePrint = () => {
    navigate(`/receipts/bill/${billId}`)
  }

  const handleSubmitToHacienda = async () => {
    try {
      const result = await submitMutation.mutateAsync(billId)
      if (result.success) {
        toast({ title: t('bills.submittedToHacienda') })
      } else {
        toast({
          title: t('common.error'),
          description: result.message || t('bills.submitError'),
          variant: 'destructive',
        })
      }
      refetch()
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || t('bills.submitError'),
        variant: 'destructive',
      })
    }
  }

  const handleDelete = () => {
    deleteMutation.mutate(billId, {
      onSuccess: () => {
        toast({
          title: t('bills.billDeleted'),
          description: t('bills.billDeletedDescription'),
        })
        navigate('/bills')
      },
      onError: () => {
        toast({
          title: t('common.error'),
          description: t('bills.deleteError'),
          variant: 'destructive',
        })
      },
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (error || !bill) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Receipt className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('bills.billNotFound')}</p>
        <p className="text-sm text-muted-foreground mb-4">
          {error ? t('bills.errorLoadingBill') : t('bills.billDoesNotExist')}
        </p>
        <div className="flex gap-2">
          {error && (
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common.retry') || 'Reintentar'}
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/bills')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('bills.backToBills')}
          </Button>
        </div>
      </div>
    )
  }

  const statusColor = BILL_STATUS_COLORS[bill.status as BillStatus] || 'bg-gray-100 text-gray-800'
  const isCredit = bill.status === BillStatus.CREDIT
  const isPending = bill.status === BillStatus.PENDING
  const hasDeposits = bill.deposits && bill.deposits.length > 0
  const canAddDeposit = isCredit && (bill.remaining_debt || 0) > 0

  // Estados donde ya NO se debe mostrar el botón de enviar
  const haciendaProcessedStates = ['accepted', 'success', 'pending', 'submitted']
  const canSubmitToHacienda = bill.status !== BillStatus.PENDING
    && bill.status !== BillStatus.CANCELLED
    && !haciendaProcessedStates.includes(bill.hacienda_status || '')

  // Misma lista blanca que RECEIVER_ACTIVITY_EDITABLE_STATES en bills.py
  const canEditReceiverActivity = bill.bill_type === BillType.INVOICE
    && bill.status !== BillStatus.PENDING
    && bill.status !== BillStatus.CANCELLED
    && [null, undefined, 'rejected', 'error', 'config_error', 'disabled', 'no_credentials', 'draft']
      .includes(bill.hacienda_status as string | null | undefined)

  // Usar el estado de Alanube (invoiceDetails) que es el más actualizado
  const realHaciendaStatus = invoiceDetails?.hacienda_status || bill.hacienda_status
  const isAccepted = realHaciendaStatus === 'accepted'

  // Nota de crédito: solo para facturas/tiquetes aceptados.
  // Se permiten múltiples NC por factura (devoluciones parciales en el tiempo).
  const canCreateCreditNote = isAccepted &&
    bill.bill_type !== BillType.CREDIT_NOTE &&
    bill.bill_type !== BillType.DEBIT_NOTE

  // Nota de débito: solo para notas de crédito aceptadas (uncomment when debit notes are implemented)
  // const canCreateDebitNote = isAccepted && bill.bill_type === BillType.CREDIT_NOTE

  return (
    <div className="space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/bills')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl md:text-2xl font-bold">
              {bill.bill_number || `#${bill.id}`}
            </h1>
            <Badge className={statusColor}>
              {BILL_STATUS_LABELS[bill.status as BillStatus] || bill.status}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {BILL_TYPE_LABELS[bill.bill_type as BillType] || bill.bill_type} - {formatDate(bill.created_at)}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 flex-wrap">
        {isPending && (
          <Button className="h-12" onClick={() => navigate(`/bills/new?continue=${billId}`)}>
            <Pencil className="h-4 w-4 mr-2" />
            {t('bills.continue')}
          </Button>
        )}
        <Button variant="outline" className="h-12" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          {t('bills.print')}
        </Button>
        {canAddDeposit && (
          <Button
            className="h-12"
            onClick={() => navigate(`/bills/${billId}/deposit`)}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('bills.addDeposit')}
          </Button>
        )}
        {canSubmitToHacienda && (
          <Button
            variant="outline"
            className="h-12"
            onClick={handleSubmitToHacienda}
            disabled={submitMutation.isPending}
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', submitMutation.isPending && 'animate-spin')} />
            {t('bills.sendToHacienda')}
          </Button>
        )}
        {canCreateCreditNote && (
          <Button
            variant="outline"
            className="h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => navigate(`/bills/${billId}/credit-note`)}
          >
            <Minus className="h-4 w-4 mr-2" />
            {t('invoicing.createCreditNote')}
          </Button>
        )}
        {/* TODO: Habilitar cuando se implemente notas de débito
        {canCreateDebitNote && (
          <Button
            variant="outline"
            className="h-12 border-blue-200 text-blue-700 hover:bg-blue-50"
            onClick={() => navigate(`/bills/${billId}/debit-note`)}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('invoicing.createDebitNote')}
          </Button>
        )}
        */}
        {isPending && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                {t('bills.deleteBill')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('bills.deleteBillConfirmTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('bills.deleteBillConfirmDescription')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>
                  {t('bills.deleteBill')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Bill Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('bills.billInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">{t('bills.documentType')}</p>
                  <p className="font-medium">{BILL_TYPE_LABELS[bill.bill_type as BillType] || bill.bill_type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('bills.paymentType')}</p>
                  <p className="font-medium">
                    {bill.payment_type ? PAYMENT_TYPE_LABELS[bill.payment_type as keyof typeof PAYMENT_TYPE_LABELS] || bill.payment_type : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('bills.paymentMethod')}</p>
                  <p className="font-medium">
                    {bill.payment_method_code
                      ? `${PAYMENT_METHOD_CODES[bill.payment_method_code] || bill.payment_method_code} (${bill.payment_method_code})`
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('bills.createdAt')}</p>
                  <p className="font-medium">{formatDateTime(bill.created_at)}</p>
                </div>
                {bill.user_name && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('bills.seller')}</p>
                    <p className="font-medium">{bill.user_name}</p>
                  </div>
                )}
                {(bill.term_days ?? 0) > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('bills.creditTerms')}</p>
                    <p className="font-medium">{bill.term_days} {t('common.days')}</p>
                  </div>
                )}
                {bill.card_transaction_number && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">{t('bills.transactionNumber')}</p>
                    <p className="font-medium font-mono">{bill.card_transaction_number}</p>
                  </div>
                )}
                {bill.activity_code && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('activities.economicActivity')}</p>
                    <p className="font-medium">
                      {bill.activity_code}
                      {tenant?.activity_codes?.find(a => a.code === bill.activity_code)?.name
                        ? ` - ${tenant.activity_codes.find(a => a.code === bill.activity_code)!.name}`
                        : ''}
                    </p>
                  </div>
                )}
                {canEditReceiverActivity ? (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">{t('activities.receiverActivity')}</p>
                    <div className="mt-1">
                      <ActivityCodeSelect
                        identification={bill.client_identification || ''}
                        value={bill.receiver_activity_code || ''}
                        valueName={catalogData?.activities?.find(a => a.code === bill.receiver_activity_code)?.name || ''}
                        onChange={(code) => saveReceiverActivity(code)}
                        disabled={updateReceiverActivityMutation.isPending}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('activities.receiverActivityEditHint')}</p>
                  </div>
                ) : bill.receiver_activity_code && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t('activities.receiverActivity')}</p>
                    <p className="font-medium">
                      {bill.receiver_activity_code}
                      {catalogData?.activities?.find(a => a.code === bill.receiver_activity_code)?.name
                        ? ` - ${catalogData.activities.find(a => a.code === bill.receiver_activity_code)!.name}`
                        : ''}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Client Info Card */}
          {(bill.client_name || bill.client_id) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t('bills.clientInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-lg break-words">
                        {bill.client_name}
                      </p>
                      {bill.client_identification && (
                        <p className="text-sm text-muted-foreground">
                          {bill.client_id_type || 'ID'}: {bill.client_identification}
                        </p>
                      )}
                    </div>
                    {bill.client_id && (
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/clients/${bill.client_id}`}>
                          {t('common.view')}
                        </Link>
                      </Button>
                    )}
                  </div>

                  {/* Client note row */}
                  <div className="rounded-md border bg-muted/30 p-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t('bills.clientNote')}
                      </p>
                      {!noteEditing && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={startEditNote}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" />
                          {bill.notes ? t('common.edit') : t('common.add')}
                        </Button>
                      )}
                    </div>
                    {!noteEditing && (
                      <p className="text-sm break-words">
                        {bill.notes || (
                          <span className="text-muted-foreground italic">
                            {t('bills.clientNoteEmpty')}
                          </span>
                        )}
                      </p>
                    )}
                    {noteEditing && (
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Input
                          type="text"
                          maxLength={60}
                          placeholder={t('bills.clientNotePlaceholder')}
                          value={noteDraft}
                          onChange={(e) => setNoteDraft(e.target.value)}
                          className="h-9"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={saveNote}
                            disabled={updateNoteMutation.isPending}
                          >
                            {t('bills.clientNoteSave')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelEditNote}
                            disabled={updateNoteMutation.isPending}
                          >
                            {t('bills.clientNoteCancel')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  {(bill.client_email || bill.client_phone) && (
                    <div className="flex flex-wrap gap-2">
                      {bill.client_email && (
                        <a
                          href={`mailto:${bill.client_email}`}
                          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Mail className="h-3 w-3" />
                          {bill.client_email}
                        </a>
                      )}
                      {bill.client_phone && (
                        <a
                          href={`tel:${bill.client_phone}`}
                          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Phone className="h-3 w-3" />
                          {bill.client_phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Exoneration Card */}
          {bill.is_exonerated && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  {t('bills.exoneration')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('bills.exonerationType')}</p>
                    <p className="font-medium">
                      {bill.exoneration_type
                        ? EXONERATION_TYPE_LABELS[bill.exoneration_type as ExonerationType] || bill.exoneration_type
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('bills.exonerationPercentage')}</p>
                    <p className="font-medium">{bill.exoneration_percentage || 100}%</p>
                  </div>
                  {bill.exoneration_number && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t('bills.exonerationNumber')}</p>
                      <p className="font-medium font-mono">{bill.exoneration_number}</p>
                    </div>
                  )}
                  {bill.exoneration_institution && (
                    <div>
                      <p className="text-xs text-muted-foreground">{t('bills.exonerationInstitution')}</p>
                      <p className="font-medium">{bill.exoneration_institution}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Line Items */}
          <Collapsible open={linesExpanded} onOpenChange={setLinesExpanded}>
            <Card>
              <CardHeader className="pb-3">
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between cursor-pointer">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Receipt className="h-4 w-4" />
                      {t('bills.lineItems')} ({bill.lines?.length || 0})
                    </CardTitle>
                    {linesExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  {/* Mobile View */}
                  <div className="space-y-3 md:hidden">
                    {bill.lines?.map((line) => (
                      <div key={line.id} className="p-3 rounded-lg bg-muted/50">
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{line.article_description}</p>
                            <p className="text-xs text-muted-foreground">{line.article_code}</p>
                          </div>
                          <p className="font-bold ml-2">{formatCurrency(line.total)}</p>
                        </div>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>{line.quantity} x {formatCurrency(line.unit_price)}</span>
                          {line.discount_percent > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              -{line.discount_percent}%
                            </Badge>
                          )}
                        </div>
                        {line.cabys_code && (
                          <p className="text-xs text-muted-foreground mt-1">
                            CABYS: {line.cabys_code}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Desktop View */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('bills.code')}</TableHead>
                          <TableHead>{t('bills.description')}</TableHead>
                          <TableHead className="text-right">{t('bills.quantity')}</TableHead>
                          <TableHead className="text-right">{t('bills.unitPrice')}</TableHead>
                          <TableHead className="text-right">{t('bills.discount')}</TableHead>
                          <TableHead className="text-right">{t('bills.total')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bill.lines?.map((line) => (
                          <TableRow key={line.id}>
                            <TableCell className="font-mono text-sm">
                              {line.article_code}
                              {line.cabys_code && (
                                <span className="block text-xs text-muted-foreground">
                                  CABYS: {line.cabys_code}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>{line.article_description}</TableCell>
                            <TableCell className="text-right">{line.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrency(line.unit_price)}</TableCell>
                            <TableCell className="text-right">
                              {line.discount_percent > 0 ? `${line.discount_percent}%` : '-'}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(line.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {(!bill.lines || bill.lines.length === 0) && (
                    <p className="text-center text-muted-foreground py-4">
                      {t('bills.noLineItems')}
                    </p>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>

        {/* Right Column - Totals and Status */}
        <div className="space-y-4">
          {/* Totals Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('bills.totals')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('bills.subtotal')}</span>
                <span>{formatCurrency(bill.subtotal)}</span>
              </div>
              {bill.total_line_discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t('bills.lineDiscounts')}</span>
                  <span>-{formatCurrency(bill.total_line_discount)}</span>
                </div>
              )}
              {bill.total_general_discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t('bills.generalDiscount')}</span>
                  <span>-{formatCurrency(bill.total_general_discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('bills.tax')} (13%)</span>
                <span>{formatCurrency(bill.total_sale_tax)}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold">
                  <span>{t('bills.total')}</span>
                  <span>{formatCurrency(bill.total)}</span>
                </div>
              </div>

              {/* Payment Info. A crédito no se pagó al emitir: lo cobrado son los abonos de abajo. */}
              {!isCredit && bill.total_client_pay && bill.total_client_pay > 0 && (
                <>
                  <div className="border-t pt-3 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{t('bills.amountPaid')}</span>
                      <span className="font-medium text-base">{formatCurrency(bill.total_client_pay)}</span>
                    </div>
                    {bill.total_client_change && bill.total_client_change > 0 && (
                      <div className="flex justify-between items-center mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-md">
                        <span className="font-medium text-green-700 dark:text-green-400">{t('bills.change')}</span>
                        <span className="font-bold text-lg text-green-700 dark:text-green-400">{formatCurrency(bill.total_client_change)}</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Credit Info */}
              {(isCredit || hasDeposits) && (
                <>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('bills.totalDeposited')}</span>
                      <span className="text-green-600">{formatCurrency(bill.total_deposited || 0)}</span>
                    </div>
                    {isCredit && (
                      <div className="flex justify-between text-sm font-medium">
                        <span className="text-muted-foreground">{t('bills.remainingDebt')}</span>
                        <span className="text-red-600">{formatCurrency(bill.remaining_debt || 0)}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Deposits Card (for credit and paid-by-deposit bills) */}
          {hasDeposits && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  {t('bills.deposits')} ({bill.deposits.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {bill.deposits.map((deposit) => (
                  <div
                    key={deposit.id}
                    className="flex justify-between items-center p-2 rounded-lg bg-muted/50"
                  >
                    <Link
                      to={`/bills/${bill.id}/deposits/${deposit.id}`}
                      className="flex-1 min-w-0 hover:opacity-80 transition-opacity"
                    >
                      <p className="font-medium">{formatCurrency(deposit.amount)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(deposit.created_at)} - {deposit.payment_type === 'credit_note' ? t('billing.creditNote') : ((deposit.payment_type && DEPOSIT_PAYMENT_LABELS[deposit.payment_type]) || deposit.payment_type)}
                      </p>
                    </Link>
                    <Button variant="ghost" size="sm" asChild title={t('receipts.print')}>
                      <Link to={`/receipts/deposit/${bill.id}/${deposit.id}`}>
                        <Printer className="h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Related Documents Card */}
          {(bill.original_invoice_id || (bill.credit_notes && bill.credit_notes.length > 0)) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  {t('bills.relatedDocuments')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* If this is a credit note, show link to original invoice */}
                {bill.original_invoice_id && (
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-muted-foreground mb-1">{t('bills.originalInvoice')}</p>
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">
                        {bill.original_invoice_number || `#${bill.original_invoice_id}`}
                      </p>
                      <Button variant="ghost" size="sm" className="h-7" asChild>
                        <Link to={`/bills/${bill.original_invoice_id}`}>
                          <ExternalLink className="h-3 w-3 mr-1" />
                          {t('common.view')}
                        </Link>
                      </Button>
                    </div>
                  </div>
                )}

                {/* If this invoice has credit notes, show them */}
                {bill.credit_notes && bill.credit_notes.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {bill.credit_notes.length === 1 ? t('bills.creditNoteSingular') : t('bills.creditNotePlural')}
                    </p>
                    {bill.credit_notes.map((cn) => (
                      <div
                        key={cn.id}
                        className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">
                              {cn.bill_number || `NC #${cn.id}`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatCurrency(cn.total)} - {formatDate(cn.created_at)}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" className="h-7" asChild>
                            <Link to={`/bills/${cn.id}`}>
                              <ExternalLink className="h-3 w-3 mr-1" />
                              {t('common.view')}
                            </Link>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Hacienda Status Card */}
          <HaciendaStatusCard
            bill={bill}
            submissions={submissions || []}
            onResend={handleSubmitToHacienda}
            isResending={submitMutation.isPending}
          />
        </div>
      </div>

    </div>
  )
}
